import {
	TrackingCheckpoint,
	TrackingLatLng,
	TrackingStep,
	TrackingStepKey,
} from './orderTracking.interface';

/** A GPS fix further than this from the step time does not locate the step. */
export const CHECKPOINT_MAX_GAP_MS = 15 * 60 * 1000;

/** `completed` happens where `unloaded` did; one pin is enough. */
const SKIPPED_STEPS: TrackingStepKey[] = ['completed'];

/** Steps that by definition happen at the pickup or delivery place. */
const PLACE_OF: Partial<Record<TrackingStepKey, 'pickup' | 'delivery'>> = {
	arrived_to_loading: 'pickup',
	loaded: 'pickup',
	unloaded: 'delivery',
};

export const checkpointSite = (
	key: TrackingStepKey,
): 'pickup' | 'delivery' | undefined => PLACE_OF[key];

interface TrailLike {
	trail?: [number, number][] | number[][];
	trailAt?: number[];
}

/** Trail point recorded closest to `time`, if close enough. */
export const nearestFix = (
	telematics: TrailLike | undefined,
	time: number,
): TrackingLatLng | undefined => {
	const trail = telematics?.trail ?? [];
	const times = telematics?.trailAt ?? [];
	if (
		!Number.isFinite(time) ||
		!trail.length ||
		times.length !== trail.length
	) {
		return undefined;
	}
	let best = -1;
	let bestGap = Infinity;
	times.forEach((at, index) => {
		const gap = Math.abs(at - time);
		if (gap < bestGap) {
			best = index;
			bestGap = gap;
		}
	});
	if (best < 0 || bestGap > CHECKPOINT_MAX_GAP_MS) return undefined;
	const [lat, lng] = trail[best];
	return Number.isFinite(lat) && Number.isFinite(lng)
		? { lat, lng }
		: undefined;
};

/**
 * One pin per completed step, where the truck actually was when the step was
 * done: the fix the driver app sent with the action, else the GPS history
 * point closest in time. Without either, steps that belong to a place fall
 * back to that place; other steps are left out rather than drawn somewhere
 * wrong.
 */
export const buildCheckpoints = (
	steps: TrackingStep[],
	telematics: TrailLike | undefined,
	pickup: TrackingLatLng,
	delivery: TrackingLatLng,
): TrackingCheckpoint[] =>
	steps.reduce<TrackingCheckpoint[]>((checkpoints, step) => {
		if (!step.completed || !step.completedAt) return checkpoints;
		if (SKIPPED_STEPS.includes(step.key)) return checkpoints;
		const fix =
			step.location ?? nearestFix(telematics, Date.parse(step.completedAt));
		const place = PLACE_OF[step.key];
		const position =
			fix ?? (place === 'pickup' ? pickup : place ? delivery : undefined);
		if (!position) return checkpoints;
		checkpoints.push({
			key: step.key,
			at: step.completedAt,
			position,
			fromGps: !!fix,
			source: step.location ? 'action' : fix ? 'gps' : 'site',
		});
		return checkpoints;
	}, []);
